/**
 * Shared currency formatter for the admin panel.
 *
 * `currency` may be either an ISO-4217 code (INR, USD, EUR, …) — in which
 * case we delegate to `Intl.NumberFormat` for locale-aware symbols and
 * grouping — or an in-game currency code (COINS, EVOTOKENS, MATCHTOKENS,
 * PREMIUMCURRENCY), which we render as plain digits plus the code.
 *
 * Locale defaults to `en-IN` so INR renders with `₹` and the
 * lakh/crore grouping admins are familiar with. Callers can override
 * via the optional `locale` argument.
 */

const ISO_CURRENCY_CODES = new Set(['INR', 'USD', 'EUR', 'GBP', 'AUD', 'CAD', 'JPY', 'SGD', 'AED']);

export function formatMoney(
  amount: number | string | null | undefined,
  currency: string | null | undefined,
  locale: string = 'en-IN'
): string {
  const code = (currency || '').toUpperCase();
  const numeric = typeof amount === 'string' ? parseFloat(amount) : (amount ?? 0);
  const safe = Number.isFinite(numeric) ? numeric : 0;

  if (ISO_CURRENCY_CODES.has(code)) {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: code,
      maximumFractionDigits: 2,
    }).format(safe);
  }

  // In-game currencies or unknown codes — render the number with locale
  // grouping and append the raw code so it stays unambiguous.
  const label = code || 'UNKNOWN';
  return `${safe.toLocaleString(locale)} ${label}`;
}

/**
 * Convenience: format a list of `{currency, amount}` rows into a single
 * human-readable string. Useful in compact statistics cards where we want
 * to show "₹18,996.40 + $50.00" instead of two separate widgets.
 */
export function formatMoneyList(
  rows: Array<{ currency: string; amount: number }>,
  locale: string = 'en-IN'
): string {
  if (!rows || rows.length === 0) return '—';
  return rows.map((r) => formatMoney(r.amount, r.currency, locale)).join(' + ');
}
